import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFilter,
  faRedo,
  faUserCircle,
  faCaretDown,
  faUser,
  faSignOutAlt,
  faCrown,
  faPlus,
  faEye,
  faCheckCircle,
  faExclamationCircle,
  faArrowUp,
  faArrowDown,
  faArrowsAltH,
  faPaperPlane,
  faTimes,
  faMicrochip,
  faSyncAlt
} from '@fortawesome/free-solid-svg-icons';
import '../scss/ErrorLog.scss';
import BaseURL from '../assets/contants/BaseURL';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

const ErrorLog = () => {
  const navigate = useNavigate();

  // Filter states
  const [filters, setFilters] = useState({
    crane: 'all',
    sensor: 'all',
    type: 'all',
    severity: 'all',
    date: 'today'
  });

  // UI States
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedError, setSelectedError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [reportForm, setReportForm] = useState({
    crane: '',
    sensorType: '',
    errorType: '',
    severity: '',
    priority: '',
    description: '',
    techDetails: ''
  });
  const [orgLogo, setOrgLogo] = useState(null);

  // Error data state
  const [errorData, setErrorData] = useState({
    severityCounts: { info: 15, warning: 10, error: 7, critical: 4 },
    sensorCounts: { vfd: 8, limit: 5, temp: 6, load: 4, rope: 3, manual: 2 },
    typeCounts: { mechanical: 12, electrical: 8, safety: 6, software: 5 },
    errors: []
  });

  // Chart refs
  const severityChartRef = useRef();
  const sensorChartRef = useRef();
  const typeChartRef = useRef();

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const getAuthToken = () => sessionStorage.getItem('Token') || localStorage.getItem('Token');

  useEffect(() => {
    const fetchOrgLogo = async () => {
      try {
        const token = getAuthToken();
        const organizationId = sessionStorage.getItem('organization_id');

        const response = await fetch(`${BaseURL}Userauth/organizations/${organizationId}/`, {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.data?.logo_url) { 
            setOrgLogo(data.data.logo_url);
          }
        }
      } catch (error) {
        console.error('Error fetching org logo:', error);
      }
    };

    fetchOrgLogo();
  }, []);

  // Sample error data
  const sampleErrors = [
    {
      id: 1,
      timestamp: '2023-06-15 08:23:45',
      craneId: 'CRN-001',
      craneName: 'Gantry Crane #1',
      code: 'VFD-ERR-2023-001',
      sensorType: 'vfd',
      errorType: 'electrical',
      severity: 'critical',
      description: 'Emergency stop activated - Overload detected during lifting operation',
      status: 'investigating',
      assignedTo: 'John Smith',
      priority: 'high',
      occurrences: 3,
      lastUpdated: '2023-06-15 09:45:22',
      resolutionDate: '2023-06-17',
      reportedBy: 'System Auto-detect',
      sensorStatus: 'critical',
      measuredValue: '142% of rated capacity',
      threshold: '110% of rated capacity',
      duration: '3.2 seconds',
      location: 'Main hoist motor'
    },
    {
      id: 2,
      timestamp: '2023-06-15 08:45:12',
      craneId: 'CRN-002',
      craneName: 'Overhead Crane #2',
      code: 'LIM-ERR-2023-002',
      sensorType: 'limit',
      errorType: 'mechanical',
      severity: 'error',
      description: 'Upper limit switch failure - Crane did not stop at max height',
      status: 'acknowledged',
      assignedTo: 'Sarah Johnson',
      priority: 'high',
      occurrences: 1,
      lastUpdated: '2023-06-15 10:15:30',
      resolutionDate: '2023-06-16',
      reportedBy: 'System Auto-detect',
      sensorStatus: 'error',
      measuredValue: 'N/A',
      threshold: 'N/A',
      duration: 'Continuous',
      location: 'Upper limit switch'
    },
    {
      id: 3,
      timestamp: '2023-06-15 09:12:33',
      craneId: 'CRN-001',
      craneName: 'Gantry Crane #1',
      code: 'TEMP-ERR-2023-001',
      sensorType: 'temp',
      errorType: 'mechanical',
      severity: 'warning',
      description: 'Motor temperature exceeding normal operating range',
      status: 'unresolved',
      assignedTo: 'Michael Chen',
      priority: 'medium',
      occurrences: 1,
      lastUpdated: '2023-06-15 09:12:33',
      resolutionDate: 'TBD',
      reportedBy: 'System Auto-detect',
      sensorStatus: 'warning',
      measuredValue: '92°C',
      threshold: '85°C',
      duration: '15 minutes',
      location: 'Hoist motor bearing'
    },
    {
      id: 4,
      timestamp: '2023-06-15 10:05:27',
      craneId: 'CRN-004',
      craneName: 'Bridge Crane #4',
      code: 'LOAD-ERR-2023-001',
      sensorType: 'load',
      errorType: 'software',
      severity: 'error',
      description: 'Load cell calibration issue detected',
      status: 'resolved',
      assignedTo: 'Emma Davis',
      priority: 'medium',
      occurrences: 2,
      lastUpdated: '2023-06-15 14:20:15',
      resolutionDate: '2023-06-15',
      reportedBy: 'System Auto-detect',
      sensorStatus: 'normal',
      measuredValue: '5.8% drift',
      threshold: '2% max drift',
      duration: 'Since last calibration',
      location: 'Main load cell'
    },
    {
      id: 5,
      timestamp: '2023-06-15 10:45:52',
      craneId: 'CRN-002',
      craneName: 'Overhead Crane #2',
      code: 'ROPE-ERR-2023-001',
      sensorType: 'rope',
      errorType: 'mechanical',
      severity: 'warning',
      description: 'Rope wear sensor triggered - Scheduled replacement needed',
      status: 'acknowledged',
      assignedTo: 'David Wilson',
      priority: 'medium',
      occurrences: 1,
      lastUpdated: '2023-06-15 11:30:05',
      resolutionDate: '2023-06-18',
      reportedBy: 'System Auto-detect',
      sensorStatus: 'warning',
      measuredValue: '8% diameter reduction',
      threshold: '7% max reduction',
      duration: 'Gradual',
      location: 'Main hoist rope'
    }
  ];

  // Chart data configurations
  const severityChartData = {
    labels: ['Information', 'Warning', 'Error', 'Critical'],
    datasets: [{
      data: [
        errorData.severityCounts.info,
        errorData.severityCounts.warning,
        errorData.severityCounts.error,
        errorData.severityCounts.critical
      ],
      backgroundColor: ['#17a2b8', '#ffc107', '#dc3545', '#721c24'],
      borderWidth: 1
    }]
  };

  const sensorChartData = {
    labels: ['VFD', 'Limit Switch', 'Temp Sensor', 'Load Cell', 'Rope Sensor', 'Manual'],
    datasets: [{
      label: 'Error Count',
      data: [
        errorData.sensorCounts.vfd,
        errorData.sensorCounts.limit,
        errorData.sensorCounts.temp,
        errorData.sensorCounts.load,
        errorData.sensorCounts.rope,
        errorData.sensorCounts.manual
      ],
      backgroundColor: ['#9b59b6', '#1abc9c', '#e74c3c', '#3498db', '#2ecc71', '#f1c40f'],
      borderWidth: 1
    }]
  };

  const typeChartData = {
    labels: ['Mechanical', 'Electrical', 'Safety', 'Software'],
    datasets: [{
      label: 'Error Count',
      data: [
        errorData.typeCounts.mechanical,
        errorData.typeCounts.electrical,
        errorData.typeCounts.safety,
        errorData.typeCounts.software
      ],
      backgroundColor: ['#e67e22', '#9b59b6', '#e74c3c', '#3498db'],
      borderWidth: 1
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      tooltip: { mode: 'index', intersect: false }
    }
  };

  const pieChartOptions = {
    ...chartOptions,
    plugins: {
      ...chartOptions.plugins,
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = Math.round((value / total) * 100);
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  const barChartOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: 'Error Count' }
      },
      x: {
        title: { display: true, text: 'Category' }
      }
    }
  };

  // Get helper functions
  const getSeverityClass = (severity) => {
    switch(severity) {
      case 'info': return 'status-info';
      case 'warning': return 'status-warning';
      case 'error': return 'status-danger';
      case 'critical': return 'status-critical';
      default: return '';
    }
  };

  const getSensorClass = (sensorType) => {
    switch(sensorType) {
      case 'vfd': return 'error-source-vfd';
      case 'limit': return 'error-source-limit';
      case 'temp': return 'error-source-temp';
      case 'load': return 'error-source-load';
      case 'rope': return 'error-source-rope';
      case 'manual': return 'error-source-manual';
      default: return '';
    }
  };

  const getErrorTypeClass = (errorType) => {
    switch(errorType) {
      case 'mechanical': return 'error-type-mechanical';
      case 'electrical': return 'error-type-electrical';
      case 'safety': return 'error-type-safety';
      case 'software': return 'error-type-software';
      default: return '';
    }
  };

  const getStatusClass = (status) => {
    switch(status) {
      case 'resolved': return 'status-resolved';
      case 'investigating': return 'status-investigating';
      case 'acknowledged': return 'status-acknowledged';
      case 'unresolved': return 'status-unresolved';
      default: return '';
    }
  };

  const getPriorityIcon = (priority) => {
    switch(priority) {
      case 'high': return <FontAwesomeIcon icon={faArrowUp} className="priority-high" />;
      case 'medium': return <FontAwesomeIcon icon={faArrowsAltH} className="priority-medium" />;
      case 'low': return <FontAwesomeIcon icon={faArrowDown} className="priority-low" />;
      default: return null;
    }
  };

  const getProgressWidth = (status) => {
    switch(status) {
      case 'unresolved': return 20;
      case 'acknowledged': return 40;
      case 'investigating': return 70;
      case 'resolved': return 100;
      default: return 0;
    }
  };

  // Filter and display errors
  const getFilteredErrors = () => {
    let filtered = [...sampleErrors];
    
    if (filters.crane !== 'all') {
      filtered = filtered.filter(error => error.craneId === filters.crane);
    }
    if (filters.sensor !== 'all') {
      filtered = filtered.filter(error => error.sensorType === filters.sensor);
    }
    if (filters.type !== 'all') {
      filtered = filtered.filter(error => error.errorType === filters.type);
    }
    if (filters.severity !== 'all') {
      filtered = filtered.filter(error => error.severity === filters.severity);
    }
    
    return filtered;
  };

  // Apply filters
  const applyFilters = () => {
    showNotification('Filters applied successfully', 'success');
    // Force re-render of table
    setErrorData({ ...errorData });
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      crane: 'all',
      sensor: 'all',
      type: 'all',
      severity: 'all',
      date: 'today'
    });
    showNotification('Filters reset', 'success');
  };

  // Handle form input changes
  const handleReportFormChange = (e) => {
    const { id, value } = e.target;
    setReportForm(prev => ({ ...prev, [id.replace('report', '').charAt(0).toLowerCase() + id.slice(1).replace('report', '')]: value }));
  };

  const handlePrioritySelect = (priority) => {
    setReportForm(prev => ({ ...prev, priority }));
  };

  // Submit error report
  const handleSubmitReport = (e) => {
    e.preventDefault();
    if (!reportForm.priority) {
      showNotification('Please select a priority level', 'error');
      return;
    }
    
    showNotification('Error report submitted successfully!', 'success');
    setIsReportModalOpen(false);
    setReportForm({
      crane: '',
      sensorType: '',
      errorType: '',
      severity: '',
      priority: '',
      description: '',
      techDetails: ''
    });
  };

  // Show error details
  const showErrorDetails = (error) => {
    setSelectedError(error);
    setIsModalOpen(true);
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      localStorage.removeItem('isLoggedIn');
      showNotification('Logged out successfully!', 'success');
      setTimeout(() => navigate('/login'), 1500);
    }
  };

  // Initialize data
  useEffect(() => {
    const sampleErrors = [
      {
        id: 1,
        timestamp: '2023-06-15 08:23:45',
        craneId: 'CRN-001',
        craneName: 'Gantry Crane #1',
        code: 'VFD-ERR-2023-001',
        sensorType: 'vfd',
        errorType: 'electrical',
        severity: 'critical',
        description: 'Emergency stop activated - Overload detected during lifting operation',
        status: 'investigating',
        assignedTo: 'John Smith',
        priority: 'high',
        occurrences: 3,
        lastUpdated: '2023-06-15 09:45:22',
        resolutionDate: '2023-06-17',
        reportedBy: 'System Auto-detect',
        sensorStatus: 'critical',
        measuredValue: '142% of rated capacity',
        threshold: '110% of rated capacity',
        duration: '3.2 seconds',
        location: 'Main hoist motor'
      },
      {
      id: 2,
      timestamp: '2023-06-15 08:45:12',
      craneId: 'CRN-002',
      craneName: 'Overhead Crane #2',
      code: 'LIM-ERR-2023-002',
      sensorType: 'limit',
      errorType: 'mechanical',
      severity: 'error',
      description: 'Upper limit switch failure - Crane did not stop at max height',
      status: 'acknowledged',
      assignedTo: 'Sarah Johnson',
      priority: 'high',
      occurrences: 1,
      lastUpdated: '2023-06-15 10:15:30',
      resolutionDate: '2023-06-16',
      reportedBy: 'System Auto-detect',
      sensorStatus: 'error',
      measuredValue: 'N/A',
      threshold: 'N/A',
      duration: 'Continuous',
      location: 'Upper limit switch'
    },
    {
      id: 3,
      timestamp: '2023-06-15 09:12:33',
      craneId: 'CRN-001',
      craneName: 'Gantry Crane #1',
      code: 'TEMP-ERR-2023-001',
      sensorType: 'temp',
      errorType: 'mechanical',
      severity: 'warning',
      description: 'Motor temperature exceeding normal operating range',
      status: 'unresolved',
      assignedTo: 'Michael Chen',
      priority: 'medium',
      occurrences: 1,
      lastUpdated: '2023-06-15 09:12:33',
      resolutionDate: 'TBD',
      reportedBy: 'System Auto-detect',
      sensorStatus: 'warning',
      measuredValue: '92°C',
      threshold: '85°C',
      duration: '15 minutes',
      location: 'Hoist motor bearing'
    },
    {
      id: 4,
      timestamp: '2023-06-15 10:05:27',
      craneId: 'CRN-004',
      craneName: 'Bridge Crane #4',
      code: 'LOAD-ERR-2023-001',
      sensorType: 'load',
      errorType: 'software',
      severity: 'error',
      description: 'Load cell calibration issue detected',
      status: 'resolved',
      assignedTo: 'Emma Davis',
      priority: 'medium',
      occurrences: 2,
      lastUpdated: '2023-06-15 14:20:15',
      resolutionDate: '2023-06-15',
      reportedBy: 'System Auto-detect',
      sensorStatus: 'normal',
      measuredValue: '5.8% drift',
      threshold: '2% max drift',
      duration: 'Since last calibration',
      location: 'Main load cell'
    },
    {
      id: 5,
      timestamp: '2023-06-15 10:45:52',
      craneId: 'CRN-002',
      craneName: 'Overhead Crane #2',
      code: 'ROPE-ERR-2023-001',
      sensorType: 'rope',
      errorType: 'mechanical',
      severity: 'warning',
      description: 'Rope wear sensor triggered - Scheduled replacement needed',
      status: 'acknowledged',
      assignedTo: 'David Wilson',
      priority: 'medium',
      occurrences: 1,
      lastUpdated: '2023-06-15 11:30:05',
      resolutionDate: '2023-06-18',
      reportedBy: 'System Auto-detect',
      sensorStatus: 'warning',
      measuredValue: '8% diameter reduction',
      threshold: '7% max reduction',
      duration: 'Gradual',
      location: 'Main hoist rope'
    },
    ];
    
    setErrorData(prev => ({ ...prev, errors: sampleErrors }));
  }, []);

  const filteredErrors = getFilteredErrors();

  return (
    <div className="error-log-container">
      <div className="dashboard-content">
        {/* Header */}
        <div className="header">
          <div className="header-left">
            <div className="header-logo">CraneIQ</div>
          </div>

        <div className="user-info-wrapper">
                    <div className="account-dropdown">
                      <div className="account-trigger" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                        {orgLogo ? (
                                          <img
                                            src={orgLogo}
                                            alt="Organization Logo"
                                            style={{
                                              width: '32px',
                                              height: '32px',
                                              borderRadius: '50%',
                                              objectFit: 'cover',
                                              border: '2px solid #4361ee'
                                            }}
                                            onError={() => setOrgLogo(null)}
                                          />
                                        ) : (
                                          <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '1.5rem', color: '#4361ee' }} />
                                        )}
                        <span>{sessionStorage.getItem('username') || 'User'}</span>
                        <FontAwesomeIcon icon={faCaretDown} />
                      </div>
                      {isDropdownOpen && (
                        <div className="account-menu show">
                          <div className="account-menu-item plan-info">
                            <FontAwesomeIcon icon={faCrown} style={{ color: 'gold' }} />
                            <span>Basic Plan</span>
                          </div>
                          <div className="account-menu-divider"></div>
                          <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                            <FontAwesomeIcon icon={faUser} /> Profile
                          </button>
                          <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                            <FontAwesomeIcon icon={faSyncAlt} /> Upgrade Plan
                          </button>
                          <div className="account-menu-divider"></div>
                          <button className="account-menu-item logout-account-btn" onClick={handleLogout}>
                            <FontAwesomeIcon icon={faSignOutAlt} /> Logout
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
        </div>

        {/* Page Title */}
        <div className="page-title">
          <h1>Error Log</h1>
          <p>System errors, warnings, and operational issues from all sensors and manual reports</p>
        </div>

        {/* Report Error Button */}
        <button className="btn btn-success report-error-btn" onClick={() => setIsReportModalOpen(true)}>
          <FontAwesomeIcon icon={faPlus} /> Report New Error
        </button>

        {/* Filter Controls */}
        <div className="log-controls">
          <div className="filter-group">
            <label className="filter-label">Crane</label>
            <select 
              className="filter-select"
              value={filters.crane}
              onChange={(e) => setFilters(prev => ({ ...prev, crane: e.target.value }))}
            >
              <option value="all">All Cranes</option>
              <option value="CRN-001">Gantry Crane #1</option>
              <option value="CRN-002">Overhead Crane #2</option>
              <option value="CRN-003">Jib Crane #3</option>
              <option value="CRN-004">Bridge Crane #4</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Sensor Type</label>
            <select 
              className="filter-select"
              value={filters.sensor}
              onChange={(e) => setFilters(prev => ({ ...prev, sensor: e.target.value }))}
            >
              <option value="all">All Sensors</option>
              <option value="vfd">VFD System</option>
              <option value="limit">Limit Switch</option>
              <option value="temp">Temperature Sensor</option>
              <option value="load">Load Cell</option>
              <option value="rope">Rope Sensor</option>
              <option value="manual">Manual Report</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Error Type</label>
            <select 
              className="filter-select"
              value={filters.type}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
            >
              <option value="all">All Types</option>
              <option value="mechanical">Mechanical</option>
              <option value="electrical">Electrical</option>
              <option value="safety">Safety</option>
              <option value="software">Software</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Severity</label>
            <select 
              className="filter-select"
              value={filters.severity}
              onChange={(e) => setFilters(prev => ({ ...prev, severity: e.target.value }))}
            >
              <option value="all">All Levels</option>
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="error">Error</option>
              <option value="critical">Critical</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Date Range</label>
            <select 
              className="filter-select"
              value={filters.date}
              onChange={(e) => setFilters(prev => ({ ...prev, date: e.target.value }))}
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>

          <div className="action-buttons">
            <button className="btn btn-primary" onClick={applyFilters}>
              <FontAwesomeIcon icon={faFilter} /> Apply
            </button>
            <button className="btn btn-secondary" onClick={resetFilters}>
              <FontAwesomeIcon icon={faRedo} /> Reset
            </button>
          </div>
        </div>

        {/* Charts Row */}
        <div className="chart-row">
          <div className="chart-containerb">
            <div className="chart-title">Errors by Severity (Last 30 Days)</div>
            <div className="chart-wrapper">
              <Pie ref={severityChartRef} data={severityChartData} options={pieChartOptions} />
            </div>
          </div>

          <div className="chart-containerb">
            <div className="chart-title">Errors by Sensor Type (Last 30 Days)</div>
            <div className="chart-wrapper">
              <Bar ref={sensorChartRef} data={sensorChartData} options={barChartOptions} />
            </div>
          </div>

          <div className="chart-containerb">
            <div className="chart-title">Errors by Type (Last 30 Days)</div>
            <div className="chart-wrapper">
              <Bar ref={typeChartRef} data={typeChartData} options={barChartOptions} />
            </div>
          </div>
        </div>

        {/* Error Log Table */}
        <div className="log-table-container">
          <table className="log-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Crane ID</th>
                <th>Crane Name</th>
                <th>Error Code</th>
                <th>Sensor</th>
                <th>Type</th>
                <th>Severity</th>
                <th>Assigned To</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredErrors.map((error) => {
                const initials = error.assignedTo !== 'Unassigned' 
                  ? error.assignedTo.split(' ').map(n => n[0]).join('')
                  : '';
                
                return (
                  <tr key={error.id}>
                    <td>{error.timestamp}</td>
                    <td>{error.craneId}</td>
                    <td>{error.craneName}</td>
                    <td><span className="error-code">{error.code}</span></td>
                    <td>
                      <span className={`error-badge ${getSensorClass(error.sensorType)}`}>
                        <span className="dot"></span> 
                        {error.sensorType.charAt(0).toUpperCase() + error.sensorType.slice(1)}
                      </span>
                    </td>
                    <td>
                      <span className={`error-badge ${getErrorTypeClass(error.errorType)}`}>
                        <span className="dot"></span> 
                        {error.errorType.charAt(0).toUpperCase() + error.errorType.slice(1)}
                      </span>
                    </td>
                    <td>
                      <span className={`error-status ${getSeverityClass(error.severity)}`}>
                        {error.severity.charAt(0).toUpperCase() + error.severity.slice(1)} 
                        {getPriorityIcon(error.priority)}
                      </span>
                    </td>
                    <td>
                      {error.assignedTo !== 'Unassigned' ? (
                        <div className="assigned-to">
                          <div className="user-avatar">{initials}</div>
                          <span>{error.assignedTo}</span>
                        </div>
                      ) : (
                        <span>Unassigned</span>
                      )}
                    </td>
                    <td>
                      <span className={`status-indicators ${getStatusClass(error.status)}`}>
                        <span className="dot"></span>
                        {error.status.charAt(0).toUpperCase() + error.status.slice(1)}
                      </span>
                    </td>
                    <td>
                      <button className="viewbtn" onClick={() => showErrorDetails(error)}>
                        <FontAwesomeIcon icon={faEye} /> View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Error Detail Modal */}
      {isModalOpen && selectedError && (
        <div className="modal show" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={() => setIsModalOpen(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <div className="modal-header">
              <h2 className="modal-title">{selectedError.code}</h2>
              <p className="modal-subtitle">{selectedError.craneName} ({selectedError.craneId})</p>
            </div>
            <div className="modal-body">
              <div className="modal-section">
                <h3 className="modal-section-title">Error Details</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Timestamp</span>
                    <span className="detail-value">{selectedError.timestamp}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Sensor Type</span>
                    <span className="detail-value">{selectedError.sensorType.toUpperCase()}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Error Type</span>
                    <span className="detail-value">{selectedError.errorType.toUpperCase()}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Severity</span>
                    <span className="detail-value">{selectedError.severity.toUpperCase()}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Priority</span>
                    <span className={`detail-value priority-${selectedError.priority}`}>
                      {selectedError.priority.toUpperCase()}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Occurrences</span>
                    <span className="detail-value">{selectedError.occurrences} time{selectedError.occurrences > 1 ? 's' : ''}</span>
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <h3 className="modal-section-title">Description</h3>
                <p>{selectedError.description}</p>
              </div>

              <div className="modal-section">
                <h3 className="modal-section-title">Technical Details</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Measured Value</span>
                    <span className="detail-value">{selectedError.measuredValue}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Threshold</span>
                    <span className="detail-value">{selectedError.threshold}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Duration</span>
                    <span className="detail-value">{selectedError.duration}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Location</span>
                    <span className="detail-value">{selectedError.location}</span>
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <h3 className="modal-section-title">Resolution Status</h3>
                <div className="progress-container">
                  <div className="progress-labels">
                    <span>Reported</span>
                    <span>{selectedError.status.toUpperCase()}</span>
                    <span>Resolved</span>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${getProgressWidth(selectedError.status)}%` }}></div>
                  </div>
                </div>

                <div className="detail-grid" style={{ marginTop: '20px' }}>
                  <div className="detail-item">
                    <span className="detail-label">Assigned To</span>
                    <span className="detail-value">{selectedError.assignedTo}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Reported By</span>
                    <span className="detail-value">{selectedError.reportedBy}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Last Updated</span>
                    <span className="detail-value">{selectedError.lastUpdated}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Estimated Resolution</span>
                    <span className="detail-value">{selectedError.resolutionDate}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary">
                <FontAwesomeIcon icon={faUser} /> Edit Status
              </button>
              <button className="btn btn-primary">
                <FontAwesomeIcon icon={faPaperPlane} /> Export Report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Error Modal */}
      {isReportModalOpen && (
        <div className="modal show" onClick={() => setIsReportModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={() => setIsReportModalOpen(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <div className="modal-header">
              <h2 className="modal-title">Report New Issue</h2>
              <p className="modal-subtitle">Help us quickly identify and resolve equipment problems</p>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmitReport}>
                <div className="form-group-with-icon report-form-group">
                  <label className="report-form-label">Crane</label>
                  <select 
                    className="report-form-select" 
                    id="reportCrane" 
                    required
                    value={reportForm.crane}
                    onChange={handleReportFormChange}
                  >
                    <option value="" disabled>Select Crane</option>
                    <option value="CRN-001">Gantry Crane #1</option>
                    <option value="CRN-002">Overhead Crane #2</option>
                    <option value="CRN-003">Jib Crane #3</option>
                    <option value="CRN-004">Bridge Crane #4</option>
                  </select>
                </div>

                <div className="form-group-with-icon report-form-group">
                  <FontAwesomeIcon icon={faMicrochip} className="form-icon" />
                  <label className="report-form-label">Component/Sensor</label>
                  <select 
                    className="report-form-select" 
                    id="reportSensorType" 
                    required
                    value={reportForm.sensorType}
                    onChange={handleReportFormChange}
                  >
                    <option value="" disabled>Select Component</option>
                    <option value="vfd">VFD System</option>
                    <option value="limit">Limit Switch</option>
                    <option value="temp">Temperature Sensor</option>
                    <option value="load">Load Cell</option>
                    <option value="rope">Rope Sensor</option>
                    <option value="other">Other/General</option>
                  </select>
                </div>

                <div className="report-form-group">
                  <label className="report-form-label">Issue Type</label>
                  <select 
                    className="report-form-select" 
                    id="reportErrorType" 
                    required
                    value={reportForm.errorType}
                    onChange={handleReportFormChange}
                  >
                    <option value="" disabled>Select Issue Type</option>
                    <option value="mechanical">Mechanical</option>
                    <option value="electrical">Electrical</option>
                    <option value="safety">Safety</option>
                    <option value="software">Software</option>
                  </select>
                </div>

                <div className="report-form-group">
                  <label className="report-form-label">Severity Level</label>
                  <select 
                    className="report-form-select" 
                    id="reportSeverity" 
                    required
                    value={reportForm.severity}
                    onChange={handleReportFormChange}
                  >
                    <option value="" disabled>Select Severity</option>
                    <option value="info">Information (No immediate impact)</option>
                    <option value="warning">Warning (Monitor closely)</option>
                    <option value="error">Error (Limited operation)</option>
                    <option value="critical">Critical (Immediate attention needed)</option>
                  </select>
                </div>

                <div className="report-form-group">
                  <label className="report-form-label">Priority</label>
                  <div className="priority-selector">
                    <div 
                      className={`priority-option low ${reportForm.priority === 'low' ? 'selected' : ''}`}
                      onClick={() => handlePrioritySelect('low')}
                    >
                      <FontAwesomeIcon icon={faArrowDown} /> Low
                    </div>
                    <div 
                      className={`priority-option medium ${reportForm.priority === 'medium' ? 'selected' : ''}`}
                      onClick={() => handlePrioritySelect('medium')}
                    >
                      <FontAwesomeIcon icon={faArrowsAltH} /> Medium
                    </div>
                    <div 
                      className={`priority-option high ${reportForm.priority === 'high' ? 'selected' : ''}`}
                      onClick={() => handlePrioritySelect('high')}
                    >
                      <FontAwesomeIcon icon={faArrowUp} /> High
                    </div>
                  </div>
                </div>

                <div className="report-form-group">
                  <label className="report-form-label">Issue Description</label>
                  <textarea 
                    className="report-form-textarea" 
                    id="reportDescription" 
                    required
                    placeholder="Describe the issue in detail including any error messages, unusual sounds, or visual indicators..."
                    value={reportForm.description}
                    onChange={handleReportFormChange}
                  ></textarea>
                </div>

                <div className="report-form-group">
                  <label className="report-form-label">Technical Details (Optional)</label>
                  <textarea 
                    className="report-form-textarea" 
                    id="reportTechDetails" 
                    placeholder="Add any technical details like measured values, thresholds, error codes, etc..."
                    value={reportForm.techDetails}
                    onChange={handleReportFormChange}
                  ></textarea>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setIsReportModalOpen(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <FontAwesomeIcon icon={faPaperPlane} /> Submit Report
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div className={`notification notification-${notification.type} show`}>
          <FontAwesomeIcon icon={notification.type === 'success' ? faCheckCircle : faExclamationCircle} />
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default ErrorLog;